import './RouteSwitch.scss';

import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { Layout, Skeleton } from 'antd';
import {
  ClinicianRoutePrefix,
  IntegrationRoutes,
  PatientRoutePrefix,
  routes,
  Routes
} from 'routes';

import { ConnectRouterState, useAppSelector } from 'lib/store/rootReducer';

import { ConnectedRxProgressBar, Footer } from 'components';

const { Content } = Layout;

import {
  applyFilter,
  routesWithWhiteBg,
  siteProgressBarRoutes
} from 'lib/constants/filters';
import { ClinicianRouter } from './route-switch-clinician/route-switch-clinician';
import { PatientRouteSwitch } from './route-switch-patient/route-switch-patient';

import { PrivacyPolicy, TermsOfService } from 'features/terms-of-service';

import { AcceptInvite } from 'features/accept-invite/accept-invite';
import { Role } from 'features/auth/types';
import { AssessmentLinkPage } from 'features/patient/assessment-link/page-assessment-link';
import { ForgotPassword } from 'features/sign-in/v2/page-forgot-password';
import { CreateAccount } from 'features/sign-in/v2/page-init-account';
import { LoginForm } from 'features/sign-in/v2/page-sign-in';
import { useAuth } from 'lib/hooks/auth/use-auth';
import { PublicRoute } from 'navigation/router/components/routes/public-route';
import { PrivateRoute } from '../components/routes/private-route';
import { RouteSwitchIntegration } from './route-switch-integration';
import { OrganiastionManagementPage } from 'features/admin/organiastion-management/page';
import RegistrationFlow from 'features/clinic/onboarding/registration/registration_flow';
import OnboardingLandingPage from 'features/clinic/onboarding/registration/page';
import { GenerateClinicSignupLinkPage } from 'features/admin/generate-clinic-signup-link/page';

export function RouteSwitch() {
  const backgroundColor = useAppSelector(state => state.uiPref.backgroundColor);
  const authState = useAuth();
  const loc = useLocation<ConnectRouterState | undefined>();
  const location = loc.pathname;

  function renderHeaderProgressBar() {
    if (
      siteProgressBarRoutes.findIndex(fn =>
        typeof fn === 'function' ? fn(location) : location === fn
      ) !== -1
    ) {
      return (
        <ConnectedRxProgressBar
          className="no-top-margin flat-ends"
          strokeLinecap="round"
        />
      );
    }
    return null;
  }

  function getBackgroundColor() {
    if (
      routesWithWhiteBg.findIndex(match =>
        typeof match === 'function' ? match(location) : match === location
      ) > -1
    ) {
      return 'white';
    }

    return backgroundColor;
  }

  // if (authState.hasError) {
  //   const err = authState.error ?? new Error('Authentication error');
  //   return (
  //     <ErrorDisplay
  //       error={err}
  //       title="Authentication Error"
  //       description="An error occurred during authentication."
  //     />
  //   );
  // }
  if (authState.isUserDataLoading) {
    return <Skeleton active />;
  }

  return (
    // TODO dynamic background color assignment through reducer
    <Layout
      className="site-layout"
      style={{ backgroundColor: getBackgroundColor() }}
    >
      {renderHeaderProgressBar()}

      <Content>
        <Switch>
          <PublicRoute
            path={'/accept/invite'}
            component={AcceptInvite}
            redirectPath={'/'}
          />
          <PublicRoute
            exact
            path={routes.onboarding.landing}
            component={OnboardingLandingPage}
            redirectPath={'/'}
          />
          <PublicRoute
            path={routes.onboarding.registration}
            component={RegistrationFlow}
            redirectPath={'/'}
          />
          <PublicRoute
            path={Routes.SignInSuffix}
            component={LoginForm}
            publicOnly
            redirectPath={'/'}
          />
          <PrivateRoute
            roles={[Role.ClinicAdmin, Role.Clinician, Role.Patient]}
            path={Routes.CreateAccountSuffix}
            component={CreateAccount}
            redirectPath={'/'}
          />
          <Route path={Routes.ForgotPassword}>
            <ForgotPassword />
          </Route>
          <Route path={Routes.TermsOfService}>
            <TermsOfService />
          </Route>
          <Route path={Routes.PrivacyPolicy}>
            <PrivacyPolicy />
          </Route>
          <PrivateRoute
            roles={[]}
            temp__extraRoles={[Role.GlobalAdmin]}
            path={routes.admin.organisationManagement}
            component={OrganiastionManagementPage}
            redirectPath={'/'}
          />
          <PrivateRoute
            roles={[]}
            temp__extraRoles={[Role.GlobalAdmin]}
            path={routes.admin.clinicSignupLinkGeneration}
            component={GenerateClinicSignupLinkPage}
            redirectPath={'/'}
          />
          <PrivateRoute
            roles={[Role.Clinician, Role.ClinicAdmin]}
            path={ClinicianRoutePrefix}
            component={ClinicianRouter}
            redirectPath={'/'}
            passwordCheck
          />
          <PrivateRoute
            roles="*"
            path={PatientRoutePrefix}
            component={PatientRouteSwitch}
            redirectPath={'/'}
            passwordCheck
          />
          <PublicRoute
            path={Routes.AssessmentLinkEntrypoint}
            redirectPath={'/'}
            component={AssessmentLinkPage}
          />
          <Route
            path={IntegrationRoutes.Prefix}
            component={RouteSwitchIntegration}
          />

          <Route
            // exact
            path={'/'}
            render={() => {
              // If there is no user
              if (!authState.user) {
                // Redirect to the patient sign in
                return <Redirect to={Routes.SignInSuffix} />;
              }

              if (authState.user.needsPassword || authState.user.isPartial) {
                return <Redirect to={Routes.CreateAccountSuffix} />;
              }

              // Otherwise the user is signed in. Redirect the user to their home page
              return (
                <Redirect
                  to={
                    [Role.Clinician, Role.ClinicAdmin].includes(
                      authState.user.role
                    )
                      ? ClinicianRoutePrefix
                      : PatientRoutePrefix
                  }
                />
              );
            }}
          />
        </Switch>
      </Content>
      {!applyFilter(location, routesWithWhiteBg) && <Footer />}
    </Layout>
  );
}
